import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { fmtMoney } from "../../lib/format";

/**
 * Phase 2D.3B.3 — „Текущо състояние" + „Статуси в момента".
 *
 * CURRENT STATE only: these numbers describe what exists right now and are
 * deliberately independent of the selected period. Archived quotations are kept
 * out of the distribution — an archive is history, not a current status.
 *
 * Money is EUR-only by contract; ``mixedCurrency`` renders one compact Bulgarian
 * warning instead of pretending other currencies were converted.
 */
const STATUS_ORDER = [
  { key: "draft",    color: "#64748b" },
  { key: "sent",     color: "#2563eb" },
  { key: "opened",   color: "#0891b2" },
  { key: "question", color: "#d97706" },
  { key: "accepted", color: "#059669" },
  { key: "declined", color: "#dc2626" },
];

function StatusTile({ statusKey, label, entry, color, onClick }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className="qw-stat"
      style={{ "--stat-accent": color }}
      onClick={onClick}
      data-testid={`status-tile-${statusKey}`}
      aria-label={label}
    >
      <span className="s-label">
        <span className="s-dot" />
        {label}
      </span>
      <span className="s-value">{(entry.count || 0).toLocaleString("bg-BG")}</span>
      <span className="s-sub">{fmtMoney(entry.value || 0, "EUR")}</span>
      <span className="s-hint">{t("dashboard.click_hint")}</span>
    </button>
  );
}

function DonutTooltip({ active, payload }) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="qw-tooltip">
      <div className="t-label">{p.label}</div>
      <div className="t-row">
        <span>{t("dashboard.aging_column_count")}</span>
        <strong>{p.count}</strong>
      </div>
      <div className="t-row">
        <span>{t("dashboard.aging_column_value")}</span>
        <strong>{fmtMoney(p.value, "EUR")}</strong>
      </div>
    </div>
  );
}

export default function DashboardStatusPanel({
  statuses,
  loading,
  onStatusClick,
}) {
  const { t } = useTranslation();

  const rows = useMemo(() => {
    const map = statuses?.statuses || {};
    return STATUS_ORDER.map((s) => ({
      ...s,
      label: t(`dashboard.status_${s.key}`),
      count: map[s.key]?.count || 0,
      value: map[s.key]?.value || 0,
    }));
  }, [statuses, t]);

  const total = useMemo(() => rows.reduce((sum, r) => sum + r.count, 0), [rows]);
  const slices = useMemo(() => rows.filter((r) => r.count > 0), [rows]);

  if (loading || !statuses) {
    return (
      <div className="qw-grid qw-current-split">
        <div className="qw-skel h-280 stretch" aria-busy="true" />
        <div className="qw-skel h-280 stretch" aria-busy="true" />
      </div>
    );
  }

  return (
    <div className="qw-grid qw-current-split" data-testid="dashboard-status-panel">
      <div className="qw-chart-card">
        <div className="head">
          <div className="h">{t("dashboard.section_status_distribution")}</div>
        </div>
        <div className="qw-stat-grid">
          {rows.map((r) => (
            <StatusTile
              key={r.key}
              statusKey={r.key}
              label={r.label}
              entry={r}
              color={r.color}
              onClick={() => onStatusClick(r.key)}
            />
          ))}
        </div>
        {statuses.mixed_currency && (
          <div className="qw-mixed-note" data-testid="status-mixed-currency">
            {t("dashboard.mixed_currency_note")}
          </div>
        )}
      </div>

      <div className="qw-chart-card" data-testid="dashboard-status-donut-card">
        <div className="head">
          <div className="h">{t("dashboard.section_statuses_now")}</div>
          <div className="sub">
            {total.toLocaleString("bg-BG")} {t("dashboard.statuses_total")}
          </div>
        </div>
        {!total ? (
          <div className="qw-empty" data-testid="status-donut-empty">
            {t("dashboard.statuses_empty")}
          </div>
        ) : (
          <div className="qw-donut-wrap">
            <div className="qw-donut-chart" data-testid="dashboard-status-donut">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={slices}
                    dataKey="count"
                    nameKey="label"
                    innerRadius="62%"
                    outerRadius="92%"
                    paddingAngle={2}
                    stroke="none"
                  >
                    {slices.map((s) => (
                      <Cell key={s.key} fill={s.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<DonutTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="qw-donut-center">
                <strong>{total.toLocaleString("bg-BG")}</strong>
                <span>{t("dashboard.statuses_total")}</span>
              </div>
            </div>
            <ul className="qw-donut-legend">
              {rows.map((r) => (
                <li key={r.key} data-testid={`status-legend-${r.key}`}>
                  <span className="qw-legend-dot" style={{ background: r.color }} />
                  <span className="l-label">{r.label}</span>
                  <span className="l-count">{r.count.toLocaleString("bg-BG")}</span>
                  <span className="l-pct">
                    {total ? ((r.count / total) * 100).toFixed(1) : "0.0"}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
